import axios from "axios";
import moment from "moment-timezone";
import { PDFDocument, PDFTextField, PDFCheckBox } from "pdf-lib";
import environment from "../config/environment.js";
import {
  AGENT_AGREEMENT_PDF_URL,
  AGENT_AGREEMENT_BROKER_NAME,
  AGENT_AGREEMENT_SALESPERSON_FIELD,
  AGENT_AGREEMENT_DATE_FIELD_NAMES,
  AGENT_AGREEMENT_AUTO_AGENT_NAME_FIELDS,
  AGENT_AGREEMENT_AUTO_BROKER_NAME_FIELDS,
  AGENT_AGREEMENT_SIGNATURE_PLACEMENT,
  AGENT_AGREEMENT_S3_UPLOAD_HELP,
} from "../constants/agentAgreement.js";
import { CURRENT_AGENT_AGREEMENT_VERSION } from "../constants/fees.js";
import user_service from "../Components/service/user_service.js";

const todayFormatted = () => moment().format("D/M/YYYY");

export async function getAgentAgreementPageCount(
  pdfUrl = AGENT_AGREEMENT_PDF_URL
) {
  const response = await fetch(pdfUrl);
  if (!response.ok) {
    throw new Error(
      AGENT_AGREEMENT_S3_UPLOAD_HELP
    );
  }

  const existingPdfBytes = await response.arrayBuffer();
  const existingPdfDoc = await PDFDocument.load(existingPdfBytes);
  return existingPdfDoc.getPageCount();
};

export async function buildSignedAgentAgreementPdf({
  pdfUrl = AGENT_AGREEMENT_PDF_URL,
  fieldValues = {},
  agentName,
  signatureDataUrl,
}) {
  const response = await fetch(pdfUrl);
  if (!response.ok) {
    throw new Error(
      AGENT_AGREEMENT_S3_UPLOAD_HELP
    );
  }

  const existingPdfBytes = await response.arrayBuffer();
  const existingPdfDoc = await PDFDocument.load(existingPdfBytes);
  const pageCount = existingPdfDoc.getPageCount();

  const signatureBytes = await fetch(signatureDataUrl).then((res) =>
    res.arrayBuffer()
  );
  const signatureImage = await existingPdfDoc.embedPng(signatureBytes);
  const placement = AGENT_AGREEMENT_SIGNATURE_PLACEMENT;

  if (pageCount > placement.secondaryPageIndex) {
    const secondaryPage = existingPdfDoc.getPage(placement.secondaryPageIndex);
    secondaryPage.drawImage(signatureImage, placement.secondarySignature);
  }

  const lastPage = existingPdfDoc.getPage(pageCount - 1);
  lastPage.drawImage(signatureImage, placement.finalSignature);

  for (let i = 0; i < pageCount; i++) {
    const page = existingPdfDoc.getPage(i);
    page.drawImage(signatureImage, placement.initialsOnEveryPage);
  }

  const form = existingPdfDoc.getForm();
  const fieldNames = form.getFields().map((field) => field.getName());
  const salespersonName =
    fieldValues[AGENT_AGREEMENT_SALESPERSON_FIELD] || agentName || "";

  for (const fieldName of fieldNames) {
    const field = form.getField(fieldName);
    if (!field) {
      continue;
    }

    if (field instanceof PDFTextField) {
      const textField = form.getTextField(fieldName);
      let value = fieldValues[fieldName] || "";

      if (fieldName === AGENT_AGREEMENT_SALESPERSON_FIELD) {
        value = salespersonName;
      } else if (AGENT_AGREEMENT_AUTO_AGENT_NAME_FIELDS.includes(fieldName)) {
        value = agentName || salespersonName;
      } else if (AGENT_AGREEMENT_AUTO_BROKER_NAME_FIELDS.includes(fieldName)) {
        value = AGENT_AGREEMENT_BROKER_NAME;
      } else if (AGENT_AGREEMENT_DATE_FIELD_NAMES.includes(fieldName)) {
        value = todayFormatted();
      }

      textField.setText(value);
    } else if (field instanceof PDFCheckBox) {
      const checkbox = form.getCheckBox(fieldName);
      if (fieldValues[fieldName]) {
        checkbox.check(fieldValues[fieldName]);
      }
    }
  }

  const modifiedPdfBytes = await existingPdfDoc.save();
  return new Blob([modifiedPdfBytes], { type: "application/pdf" });
}

export async function uploadSignedAgentAgreement({
  contactId,
  paperworkId,
  signedPdfBlob,
  fileName = "broker-agent-agreement-signed.pdf",
}) {
  const pdfFormData = new FormData();
  pdfFormData.append("file", signedPdfBlob, fileName);

  const uploadResponse = await axios.post(environment.uploadUrl, pdfFormData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  const documentUrl = uploadResponse.data;

  await user_service.contactUpdate(contactId, {
    onboard_signature: documentUrl,
    agent_agreement_version: CURRENT_AGENT_AGREEMENT_VERSION,
  });

  if (paperworkId) {
    await user_service.agentpaperworkDocument({
      agentId: contactId,
      paperwork_id: paperworkId,
      documenturl: documentUrl,
    });
  }

  return documentUrl;
}

export async function signAndUploadAgentAgreement({
  contactId,
  paperworkId,
  agentName,
  fieldValues,
  signatureDataUrl,
  pdfUrl = AGENT_AGREEMENT_PDF_URL,
}) {
  const signedPdfBlob = await buildSignedAgentAgreementPdf({
    pdfUrl,
    fieldValues,
    agentName,
    signatureDataUrl,
  });

  const documentUrl = await uploadSignedAgentAgreement({
    contactId,
    paperworkId,
    signedPdfBlob,
  });

  return documentUrl;
}
